import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import "../styles/SimilarEventsPanel.css";
  
const API_BASE = process.env.REACT_APP_API_BASE_URL;

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1506784983877-45594efa4cbe?auto=format&fit=crop&w=1200&q=80";

export default function SimilarEventsPanel({ event }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

    const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // ID kategorii z eventu
  const categoryNames = useMemo(() => {
    if (!event?.category_names) return [];
    return event.category_names.map((c) =>
      typeof c === "object" && c !== null ? c.id : c
    );
  }, [event]);

  useEffect(() => {
    // brak eventu / daty / kategorii → nic nie pobieramy
    if (!event || !event.start_datetime || categoryNames.length === 0) {
      setEvents([]);
      return;
    }

    const controller = new AbortController();

    const fetchSimilar = async () => {
      try {
        setLoading(true);
        setError("");

        const start = dayjs(event.start_datetime);
        const from = start.subtract(2, "day").format("YYYY-MM-DD");
        const to = start.add(2, "day").format("YYYY-MM-DD");

        const params = new URLSearchParams();

        // 🔧 DOSTOSUJ NAZWY PARAMETRÓW DO SWOJEGO API:
        params.set("category", categoryNames.join(",")); // np. ?categories=1,2,3
        params.set("date_from", from);                  // np. ?start_from=...
        params.set("date_to", to);                      // np. ?start_to=...
        params.set("sort", "popular");           // np. -popularity_score
        params.set("page_size", "6");   
                         // jeśli masz paginację
        if( event.location_type == "live"){
          params.set("lat",event.lat);
          params.set("lng",event.lng);
          params.set("radius_km",20)
        }

        const res = await fetch(`${API_BASE}/api/events/?${params.toString()}`, {
          signal: controller.signal,
        });
        if (!res.ok) throw new Error("Błąd pobierania podobnych wydarzeń");

        const data = await res.json();

        const items = Array.isArray(data) ? data : data.results || [];

        const filtered = items.filter((e) => e.id !== event.id);
        setEvents(filtered);
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error(err);
        setError("Nie udało się załadować podobnych wydarzeń.");
      } finally {
        setLoading(false);
      }
    };

    fetchSimilar();

 return () => controller.abort();
  }, [event?.id, event?.start_datetime, categoryNames]);

  // Aktualizacja informacji o możliwości scrollowania
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const updateScrollState = () => {
      setCanScrollLeft(el.scrollLeft > 0);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };

    updateScrollState();
    el.addEventListener("scroll", updateScrollState);
    window.addEventListener("resize", updateScrollState);

    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [events.length]);

  const scrollByCard = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    const firstCard = el.querySelector(".similar-events-card");
    const cardWidth = firstCard
      ? firstCard.getBoundingClientRect().width + 16 // 16px za gap
      : 280;

    el.scrollBy({
      left: direction * cardWidth,
      behavior: "smooth",
    });
  };

  // --- RENDER ---

  if (!event) return null;

  if (loading) {
    return (
      <div className="container mt-5">
        <h5 className="mb-3">Podobne wydarzenia</h5>
        <div className="text-muted small">Ładowanie...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5">
        <h5 className="mb-3">Podobne wydarzenia</h5>
        <div className="text-danger small">{error}</div>
      </div>
    );
  }

  if (!events.length) return null;

  return (
    <div className="container mt-5">
      <h5 className="mb-3">Podobne wydarzenia</h5>

      <div className="position-relative similar-events-row">
        {canScrollLeft && (
          <button
            type="button"
            className="btn btn-light shadow-sm similar-events-arrow similar-events-arrow-left"
            onClick={() => scrollByCard(-1)}
          >
            <i className="bi bi-chevron-left" />
          </button>
        )}

        <div className="similar-events-scroll" ref={scrollRef}>
          {events.map((e) => (
            <div
              key={e.id}
              className="similar-events-card"
              onClick={() => navigate(`/event/${e.id}`)}
              role="button"
            >
              <div className="card event-card border-0 shadow-sm h-100">
                <div
                  className="cover"
                  style={{
                    backgroundImage: `url(${e.image || FALLBACK_IMAGE})`,
                  }}
                />
                <div className="card-body">
                  <h6 className="fw-semibold">{e.name}</h6>
                  <p className="small text-muted mb-0">
                    <i className="bi bi-geo-alt"></i>{" "}
                    {e.venue_name || "Brak danych"}
                  </p>
                  <p className="small text-muted mb-0">
                    <i className="bi bi-calendar"></i>{" "}
                    {e.start_datetime
                      ? dayjs(e.start_datetime).format("YYYY-MM-DD HH:mm")
                      : ""}
                  </p>
                  <p>
                  {e.category_names
                            .filter((t) => t !== e.primary)
                            .map((t) => (
                              <span key={t} className="badge text-bg-primary text-white px-2 py-1 rounded-pill">
                                {t}
                              </span>
                            ))}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {canScrollRight && (
          <button
            type="button"
            className="btn btn-light shadow-sm similar-events-arrow similar-events-arrow-right"
            onClick={() => scrollByCard(1)}
          >
            <i className="bi bi-chevron-right" />
          </button>
        )}
      </div>
    </div>
  );
}
